import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HereMapService } from '../here-map.service';
import { WindowRefService } from 'i2g-ng-utils';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * HERE maps Javascript API Key
 * Same for all environments
 *
 * @see https://developer.here.com/
 */
const API_KEY = 'IGFGxJ62jAkEyxVKF7xG5FxIOrvgew311UGB1AKArlk';

@Component({
  selector: 'i2g-here-map',
  templateUrl: 'here-map.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HereMapService],
})
export class HereMapComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Latitude
   */
  @Input()
  set lat(lat: number) {
    this._lat.next(lat);
  }

  /**
   * Longitude
   */
  @Input()
  set lng(lng: number) {
    this._lng.next(lng);
  }

  /**
   * Zoom level
   */
  @Input()
  set zoom(zoom: number) {
    this._zoom.next(zoom);
  }

  hostWidth: string;
  hostHeight: string;

  private _lat = new BehaviorSubject<number>(undefined);
  private _lng = new BehaviorSubject<number>(undefined);
  private _zoom = new BehaviorSubject<number>(10);
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  @ViewChild('container', { static: true }) mapContainer: ElementRef;

  constructor(
    private _hereMapService: HereMapService,
    private _hostElement: ElementRef,
    private _windowRefService: WindowRefService
  ) {}

  ngOnInit() {
    const computedStyle = this._windowRefService.nativeWindow.getComputedStyle(
      this._hostElement.nativeElement
    );
    this.hostWidth = computedStyle.getPropertyValue('width');
    this.hostHeight = computedStyle.getPropertyValue('height');

    // using watch methods instead of ngOnChanges because to wait for map to be initialized first in
    // ngAfterViewInit but ngOnChanges is executed before that
    this._watchLatLngChanges();
    this._watchZoomChanges();
  }

  ngAfterViewInit() {
    // execute after here map js lib is loaded
    this._hereMapService.createMap(API_KEY, this.mapContainer.nativeElement);
  }

  private _watchLatLngChanges() {
    combineLatest([this._hereMapService.mapReady$, this._lat, this._lng])
      .pipe(takeUntil(this._destroyed))
      .subscribe(([mapReady, lat, lng]) => {
        if (mapReady) {
          this._hereMapService.setCenter(lat, lng);
        }
      });
  }

  private _watchZoomChanges() {
    combineLatest([this._hereMapService.mapReady$, this._zoom])
      .pipe(takeUntil(this._destroyed))
      .subscribe(([mapReady, zoom]) => {
        if (mapReady) {
          this._hereMapService.setZoom(zoom);
        }
      });
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
